import { Component } from '@theme/component';

/**
 * Kahla v2 product card.
 *
 * Two jobs:
 *
 * 1. Tell the form which size was clicked. Horizon's submit handler builds the
 *    request body with `new FormData(form)`, which excludes the submitter
 *    button, and it only writes the hidden variant field when a variant picker
 *    changes — a card has no variant picker. So without this the form posts
 *    id="" and the server answers 422 "Cannot find variant". Click fires before
 *    submit (for pointer, Enter and Space alike), so copying the id here is
 *    enough and the theme's own cart logic, live region and error handling all
 *    still apply.
 *
 * 2. Open and close the size strip on touch, where there is no hover to do it.
 */
class KhProductCard extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    this.addEventListener('pointerenter', this.#loadBack, { once: true });
    this.addEventListener('focusin', this.#loadBack, { once: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('pointerenter', this.#loadBack);
    this.removeEventListener('focusin', this.#loadBack);
    this.#unbindDismiss();
  }

  /**
   * Loads the back photo the first time the card is hovered or focused, and
   * only where hover actually exists. The markup ships it with no src, so on a
   * touch device — which can never show the swap — it is never fetched, and on
   * a pointer device it is not fetched at page load either. `loading="lazy"`
   * was measured and does not defer: 24 of 24 fetched at load on a fast
   * connection, 20 of 24 throttled.
   */
  #loadBack = () => {
    if (!window.matchMedia('(hover: hover)').matches) return;

    const img = /** @type {HTMLImageElement | null} */ (this.querySelector('.kh-prod__backimg[data-src]'));
    if (!img) return;

    const { src, srcset } = img.dataset;
    if (srcset) img.srcset = srcset;
    if (src) img.src = src;

    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
  };

  /** @param {Event} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const size = target?.closest?.('.kh-prod__sz');
    if (!(size instanceof HTMLButtonElement) || size.disabled) return;

    const variantId = size.dataset.variantId;
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[ref="variantId"]'));

    if (!variantId || !input) {
      // Nothing sane to submit — let the link take them to the product page
      // rather than posting an empty id and surfacing a cart error.
      event.preventDefault();
      return;
    }

    input.value = variantId;
  };

  /**
   * Toggles the size strip. Touch only — the + that calls this is
   * `display: none` wherever hover exists.
   *
   * @param {Event} event
   */
  toggleStrip = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#setOpen(!this.classList.contains('is-quickopen'));
  };

  /** @param {boolean} open */
  #setOpen(open) {
    this.classList.toggle('is-quickopen', open);
    this.querySelector('.kh-prod__add')?.setAttribute('aria-expanded', String(open));

    if (open) {
      const first = /** @type {HTMLButtonElement | null} */ (
        this.querySelector('.kh-prod__sz:not([disabled])')
      );
      first?.focus();
      this.#bindDismiss();
    } else {
      this.#unbindDismiss();
    }
  }

  // Once the strip is open on touch the + is hidden, so without these the
  // customer has no way back out of it.
  #bindDismiss() {
    document.addEventListener('click', this.#onOutside, true);
    document.addEventListener('keydown', this.#onKeydown);
  }

  #unbindDismiss() {
    document.removeEventListener('click', this.#onOutside, true);
    document.removeEventListener('keydown', this.#onKeydown);
  }

  /** @param {Event} event */
  #onOutside = (event) => {
    const target = /** @type {Node} */ (event.target);
    if (!this.contains(target)) this.#setOpen(false);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    this.#setOpen(false);
    /** @type {HTMLElement | null} */ (this.querySelector('.kh-prod__add'))?.focus();
  };
}

if (!customElements.get('kh-product-card')) {
  customElements.define('kh-product-card', KhProductCard);
}

export default KhProductCard;
